import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { DataTable, Disclose, Loading, freshToken } from "@lopie/kernel-ui";

import SandboxUpkeep from "./SandboxUpkeep.jsx";
import { sandboxPollDelay } from "./poll.js";
import { REVISION_STEP_WORD, countSaves, previousBuildLine, replacedLine, revisionOf } from "./revision.js";
import { followHead } from "./sandbox.js";
import {
  GRANT_KIND,
  STATE_WORD,
  accessLine,
  STEP_WORD,
  callsLine,
  egressLine,
  egressReading,
  gatewayView,
  groupsOf,
  headline,
  minutesWord,
  panelFingerprint,
  personaWord,
  phaseOf,
  progressOf,
  recoveryWords,
  refusalWords,
  runOf,
  sandboxState,
  screenshotPath,
  screenshotQuery,
  screenshotSource,
  screenshotWhy,
  screenshotsOf,
  steps,
  submitGate,
  viewerUrl,
  when,
  checkWord,
} from "./sandboxPanel.js";

/**
 * The sandbox beside the chat (LO-187): what the Builder agent sees, for the
 * person who asked for the app.
 *
 * **Everything is drawn from the kernel's own answers**, the same ones the MCP
 * tools read: the request, the QA report and its gate, the call log and the
 * egress log. There is no report format of Builder's own here. A check is
 * shown with the title, detail and reproduction steps the run stored; the
 * words a pass is called by are the kernel's (`label`); and the Submit button
 * reads the kernel's `gate.passed_for_current`, not a rule copied from it.
 *
 * **A qualification goes on the thing it qualifies.** A pass is never a bare
 * "passed": the chip is followed, on the same line, by the sentence that says
 * what it was a pass of, and what a pass leaves unchecked is one click under it.
 * An empty egress log says it is not proof of nothing, where the figure is.
 *
 * **A refusal is information.** A disabled Submit button says which run is
 * missing or failed, in words beside it; a kernel that refuses a submit is
 * quoted, never paraphrased and never an empty banner.
 */

export const HELD = { overflowWrap: "anywhere", minWidth: 0 };
export const MONO = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: "0.75rem" };

const STATE_TONE = { ready: "success", failed: "error", stale: "warning", provisioning: "info", seeding: "info" };
const STEP_TONE = { done: "success", failed: "error", active: "info", waiting: "default" };
const RUN_TONE = { passed: "success", failed: "error", error: "warning", running: "info", stale: "warning", inconclusive: "warning", none: "default" };

/** The kernel's refusal, whole: its sentence, then the code it sent and the
 *  status, so nobody has to parse a sentence to say what happened. */
export function Said({ failure, title }) {
  const said = refusalWords(failure);
  return (
    <Alert severity="error" variant="outlined" role="alert">
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      <Typography variant="body2" sx={HELD}>{said.text || "No reason was given."}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, ...HELD }}>
        {said.status ? `status ${said.status}` : "no answer"}
        {said.code ? <> · <code>{said.code}</code></> : null}
      </Typography>
    </Alert>
  );
}

/**
 * A sandbox that did not come up in time (LO-192): what the provisioner recorded
 * about each attempt, in the provider's own words. The title says what happened,
 * so the box is never the colour alone; the reasons are each attempt's own
 * sentence, shown as sent and not reworded; and what the reason does not prove
 * is on it, where a reader meets it.
 */
export function RecoveryNotice({ recovery }) {
  const words = recoveryWords(recovery);
  if (!words) return null;
  const failed = recovery.state === "failed";
  return (
    <Alert severity={words.severity} variant="outlined" role={failed ? "alert" : "status"}>
      <AlertTitle>{words.title}</AlertTitle>
      <Stack spacing={0.75}>
        {(recovery.stalls || []).map((s) => (
          <Typography key={s.attempt} variant="body2" sx={HELD}>
            Attempt {s.attempt} had not come up after {minutesWord(s.after_seconds) || "its time limit"}
            {s.resource ? <>, <span style={MONO}>{s.resource}</span></> : null}
            {s.reason ? <>: <span style={MONO}>{s.reason}</span></> : null}
          </Typography>
        ))}
        {recovery.does_not_prove ? (
          <Typography variant="caption" color="text.secondary" sx={HELD}>{recovery.does_not_prove}</Typography>
        ) : null}
      </Stack>
    </Alert>
  );
}

// -- reading it ---------------------------------------------------------------------

const settle =(promise) => promise.then((body) => ({ body }), (failure) => ({ failure }));
export const OPEN = ["draft", "pending"];

/** One read of a request at a time. A second ask for the same request while the
 *  first is on its way (two panels for one request, an effect mounted twice by a
 *  development build, Check again pressed during a look) joins it and gets what it
 *  gets, so the API is asked once and not once per asker. */
const inflight = new Map();
export function joined(key, run) {
  const held = inflight.get(key);
  if (held) return held;
  const started = run().finally(() => {
    if (inflight.get(key) === started) inflight.delete(key);
  });
  inflight.set(key, started);
  return started;
}

/**
 * The request, the control plane's half and the QA result, read together and
 * again on a schedule that suits what the sandbox is waiting for
 * (`sandboxPollDelay`): a provisioning instance every ten seconds at first, a
 * QA run every five, and not at all once nothing can change by itself.
 *
 * Starts from the request id it is given and follows `superseded_by` to the
 * request that is live, because the chat names the draft its agent last saw and
 * a draft is replaced whenever somebody saves new sandbox inputs.
 */
export function useSandbox(get, requestId) {
  const [data, setData] = useState({ loading: true });
  const [quiet, setQuiet] = useState(false);
  const [look, setLook] = useState({ now: () => {} });

  useEffect(() => {
    // An app with no request has no sandbox to look at (LO-207: the app's page reads
    // one whether or not there is a draft). Said as nothing to read, not as a failure.
    if (!requestId) {
      setData({ loading: false, head: "", empty: true });
      setQuiet(false);
      setLook({ now: () => {} });
      return undefined;
    }
    let live = true;
    let timer;
    let unchanged = 0;
    let last = "";
    let head = requestId;
    let phase = "waiting";
    setData({ loading: true });
    setQuiet(false);

    const read = async (from) => {
      let at = from;
      const out = { loading: false, head: from };
      try {
        const request = await followHead((id) => get(`/manifest-requests/${id}`), from);
        out.request = request;
        at = request?.request_id || from;
        out.head = at;
      } catch (failure) {
        out.requestFailure = failure;
      }
      if (out.request && OPEN.includes(out.request.state)) {
        const [control, qa] = await Promise.all([
          settle(get(`/builder/sandboxes/${at}`)),
          settle(get(`/sandboxes/${at}/qa`)),
        ]);
        // Not having one yet is an answer; being unable to ask is not.
        if (control.failure && control.failure.error !== "no_sandbox") out.controlFailure = control.failure;
        out.control = control.body || null;
        out.qa = qa.body || null;
        out.qaFailure = qa.failure || null;
      }
      out.box = sandboxState(out.request, out.control);
      return out;
    };

    const schedule = (out) => {
      clearTimeout(timer);
      phase = phaseOf({ request: out.request, requestFailure: out.requestFailure, box: out.box, qa: out.qa });
      const delay = sandboxPollDelay({ phase, hidden: document.hidden, unchanged });
      if (delay === null) {
        setQuiet(phase !== "done" && !document.hidden);
        return;
      }
      setQuiet(false);
      timer = setTimeout(loop, delay);
    };

    const loop = async () => {
      const out = await joined(head, () => read(head));
      head = out.head || head;
      if (!live) return;
      setData(out);
      const seen = panelFingerprint(out);
      unchanged = seen === last ? unchanged + 1 : 0;
      last = seen;
      schedule(out);
    };

    const now = () => {
      if (!live) return;
      unchanged = 0;
      clearTimeout(timer);
      loop();
    };
    // A tab nobody can see is not looked at, and is looked at the moment it is,
    // unless there was nothing left to wait for.
    const onVisibility = () => {
      if (document.hidden) clearTimeout(timer);
      else if (phase !== "done") now();
    };
    document.addEventListener("visibilitychange", onVisibility);
    setLook({ now });
    loop();
    return () => {
      live = false;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [get, requestId]);

  return { ...data, quiet, again: look.now };
}

/** A log read when asked for or when a run has finished, and read again then. */
export function useLog(get, path, key) {
  const [state, setState] = useState({ phase: "idle" });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, phase: "loading", failure: null }));
    try {
      setState({ phase: "ready", body: await get(path) });
    } catch (failure) {
      setState({ phase: "failed", failure });
    }
  }, [get, path]);
  useEffect(() => {
    if (key) load();
  }, [key, load]);
  return { ...state, load };
}

// -- small parts ----------------------------------------------------------------------

/** A state in a word, outlined: a failure is a heavier border and weight and
 *  not a filled chip, because white on the dark scheme's red is under 4.5:1 and
 *  the word must read in both schemes.
 *
 *  The word is the tone mixed toward the text colour, not the tone itself: an
 *  outlined chip's word in the palette's own orange or blue is about 3:1 on the
 *  light scheme's paper, and mixing toward `text.primary` darkens it there and
 *  lightens it on the dark scheme, so the word clears 4.5:1 in both while the
 *  border keeps the tone. */
export function Mark({ tone, strong, label }) {
  const toned = tone && tone !== "default";
  return (
    <Chip size="small" variant="outlined" color={tone} label={label}
      sx={{
        ...(toned ? {
          color: (theme) => {
            const palette = (theme.vars || theme).palette;
            return `color-mix(in srgb, ${palette[tone].main} 60%, ${palette.text.primary})`;
          },
        } : {}),
        ...(strong ? { fontWeight: 700, borderWidth: 2 } : {}),
      }} />
  );
}

/** A real ring on whatever has the keyboard, in the panel, in both schemes.
 *  MUI marks a focused button with a ripple and a shadow, which a button styled
 *  to look unavailable loses, and a link has neither. */
export const FOCUS_RING = {
  "& :focus-visible": {
    outline: (theme) => `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: "2px",
  },
};

/** A button that says it is unavailable and stays where the keyboard can find
 *  it, so the reason beside it can be reached too. `disabled` would take it out
 *  of the tab order, and with it the only thing that says why. */
export function GuardedButton({ can, reasonId, onClick, children, ...rest }) {
  return (
    <Button
      variant="contained"
      size="small"
      aria-disabled={!can}
      aria-describedby={can ? undefined : reasonId}
      onClick={(event) => { if (can) onClick(event); }}
      sx={can ? undefined : {
        bgcolor: "action.disabledBackground",
        color: "text.disabled",
        boxShadow: "none",
        cursor: "not-allowed",
        "&:hover": { bgcolor: "action.disabledBackground", boxShadow: "none" },
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

/** One screenshot, fetched only when asked for, and never left blank: a link
 *  that has expired or a route that refused is said, with what the code means
 *  and then the kernel's own words, and a way to ask again.
 *
 *  The link is short-lived (the kernel says for how long) and the bucket has no
 *  CORS, so it is only ever an `<img src>`: never fetched here. An image that
 *  fails to load has most likely outlived its link, so one automatic fresh
 *  request is made before the failure is shown. */
function Screenshot({ get, requestId, runId, path, label }) {
  const [phase, setPhase] = useState("idle");
  const [source, setSource] = useState(null);
  const [why, setWhy] = useState("");
  const renewed = useRef(false);
  const load = async (renewing = false) => {
    if (!renewing) renewed.current = false;
    setPhase("loading");
    setWhy("");
    try {
      const found = screenshotSource(
        await get(screenshotPath(requestId, path), screenshotQuery(runId)));
      if (!found) throw new Error("The kernel did not send a link to the image.");
      setSource(found);
      setPhase("shown");
    } catch (failure) {
      setWhy(screenshotWhy(failure));
      setPhase("failed");
    }
  };
  const name = label || path;
  return (
    <Box sx={{ mt: 1, minWidth: 0 }}>
      {phase === "idle" ? (
        <Button size="small" variant="outlined" onClick={() => load()}>
          Show screenshot{label ? `: ${label}` : ""}
        </Button>
      ) : null}
      {phase === "loading" ? <Loading label={`Loading the screenshot ${name}`} minHeight={60} size={20} /> : null}
      {phase === "shown" ? (
        <Box component="figure" sx={{ m: 0, minWidth: 0 }}>
          <Box
            component="img"
            src={source.url}
            alt={`Screenshot from the QA run: ${name}`}
            onError={() => {
              if (!renewed.current) {
                renewed.current = true;
                load(true);
                return;
              }
              setWhy("The link opened no image. It is short-lived and may have expired.");
              setPhase("failed");
            }}
            sx={{ display: "block", maxWidth: "100%", height: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}
          />
          <Typography component="figcaption" variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, ...HELD }}>
            {name}. Taken by the QA run in the sandbox, on synthetic data.{" "}
            <Link href={source.url} target="_blank" rel="noreferrer">Open full size</Link>
          </Typography>
        </Box>
      ) : null}
      {phase === "failed" ? (
        <Alert severity="warning" variant="outlined" role="alert"
          action={<Button size="small" color="inherit" onClick={() => load()}>Try again</Button>}>
          This screenshot could not be loaded{why ? `: ${why}` : "."}
        </Alert>
      ) : null}
    </Box>
  );
}

export function Steps({ list }) {
  const value = progressOf(list);
  const said = list.map((s) => `${s.label} ${STEP_WORD[s.state]}`).join(", ");
  return (
    <Stack spacing={1}>
      <LinearProgress variant="determinate" value={value} aria-label="Sandbox progress"
        aria-valuetext={`${value} percent. ${said}`} />
      <Box component="ol" sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1, m: 0, p: 0, listStyle: "none" }}>
        {list.map((s) => (
          <Box component="li" key={s.key} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{s.label}</Typography>
            <Mark tone={STEP_TONE[s.state]} strong={s.state === "failed"} label={STEP_WORD[s.state]} />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

// -- a chat that has moved past the draft (LO-206) ------------------------------------------

const REVISION_TONE = { done: "success", active: "info", waiting: "default", skipped: "default" };

/** Why this sandbox is not the one being made. Informational and not an error: the
 *  title says what happened and the lines name the build, the times, and what
 *  comes next, so it is never the colour alone. */
function RevisionNotice({ revision }) {
  return (
    <Alert severity={revision.severity} variant="outlined" role="status">
      <AlertTitle>{revision.title}</AlertTitle>
      <Stack spacing={0.75}>
        {revision.lines.map((line, i) => (
          <Typography key={i} variant="body2" sx={HELD}>{line}</Typography>
        ))}
      </Stack>
    </Alert>
  );
}

/** The new version's progress, from the tools the builder has run since: a step it
 *  did not run for this revision says so and is never called done, and the new
 *  sandbox is always waiting, because it starts when the draft is saved. */
function RevisionSteps({ list }) {
  // The sandbox is the last step and is not this panel's, so the bar counts the four before it.
  const counted = list.slice(0, 4).map((s) => (s.state === "skipped" ? { ...s, state: "done" } : s));
  const value = progressOf(counted);
  const said = list.map((s) => `${s.label} ${REVISION_STEP_WORD[s.state]}`).join(", ");
  return (
    <Stack spacing={1} component="section" aria-label="The new version">
      <Typography variant="subtitle2">The new version</Typography>
      <LinearProgress variant="determinate" value={value} aria-label="New version progress"
        aria-valuetext={`${value} percent of the way to a saved draft. ${said}`} />
      <Box component="ol" sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 1, m: 0, p: 0, listStyle: "none" }}>
        {list.map((s) => (
          <Box component="li" key={s.key} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{s.label}</Typography>
            <Mark tone={REVISION_TONE[s.state]} label={REVISION_STEP_WORD[s.state]} />
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

// -- the report -----------------------------------------------------------------------

function Reproduction({ steps: list }) {
  if (!list?.length) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
        How to reproduce it
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { typography: "body2", ...HELD } }}>
        {list.map((step, i) => <li key={i}>{step}</li>)}
      </Box>
    </Box>
  );
}

function CheckRow({ check, get, requestId, runId }) {
  const failed = check.status === "fail";
  // A failure that could not be judged, and not one the app caused (LO-193): the
  // word says so, and the colour is the attention one and not the error one.
  const unjudged = failed && check.inconclusive === true;
  const findings = (check.evidence?.findings || []);
  return (
    <Box component="li" sx={{ listStyle: "none", py: 1, minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Mark tone={unjudged ? "warning" : failed ? "error" : check.status === "skipped" ? "warning" : "success"}
          strong={failed} label={checkWord(check.status, check)} />
        <Typography variant="body2" sx={{ fontWeight: 600, ...HELD }}>{check.title || check.id}</Typography>
      </Stack>
      {check.route ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, ...HELD }}>
          Seen at <span style={MONO}>{check.route}</span>
        </Typography>
      ) : null}
      {check.detail ? <Typography variant="body2" sx={{ mt: 0.5, ...HELD }}>{check.detail}</Typography> : null}
      {failed ? <Reproduction steps={check.reproduction} /> : null}
      {findings.length > 1 ? (
        <Disclose sx={{ mt: 1 }} label="All findings" count={findings.length}
          summary={`${check.evidence?.failed ?? findings.length} findings for this check, of ${check.evidence?.judged ?? "?"} judged.`}>
          <Stack component="ul" spacing={1} sx={{ m: 0, p: 0, listStyle: "none" }}>
            {findings.map((f, i) => (
              <Box component="li" key={i} sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={HELD}>
                  {f.route ? <span style={MONO}>{f.route}</span> : null}{f.route && f.detail ? ": " : ""}{f.detail}
                </Typography>
                <Reproduction steps={f.steps} />
                {f.screenshot ? <Screenshot get={get} requestId={requestId} runId={runId} path={f.screenshot} label={`${check.title}, finding ${i + 1}`} /> : null}
              </Box>
            ))}
          </Stack>
        </Disclose>
      ) : null}
      {check.screenshot ? (
        <Screenshot get={get} requestId={requestId} runId={runId} path={check.screenshot} label={check.title || check.id} />
      ) : null}
    </Box>
  );
}

function Routes({ persona }) {
  const routes = persona?.routes || [];
  if (!routes.length) return null;
  return (
    <DataTable
      rows={routes}
      getRowKey={(r, i) => `${r.route}:${i}`}
      columns={[
        { key: "route", label: "Screen", mono: true },
        { key: "calls", label: "Kernel calls", align: "right" },
        { key: "refusals", label: "Refusals", align: "right" },
        { key: "data", label: "Showed data", render: (r) => (r.data ? "yes" : "no") },
      ]}
    />
  );
}

/** One part of the report: the whole run's checks, or one persona's. What is
 *  wrong is at full size and what passed is one click in, because a long list
 *  of passes is where the failure would get lost. */
export function Group({ group, get, requestId, runId }) {
  const failed = group.checks.filter((c) => c.status === "fail");
  const unjudged = failed.filter((c) => c.inconclusive === true);
  const skipped = group.checks.filter((c) => c.status === "skipped");
  const passed = group.checks.filter((c) => c.status === "pass");
  const p = group.persona;
  const shots = p ? screenshotsOf(group) : [];
  const tone = group.status === "passed" ? "success" : group.status === "failed" ? "error" : group.status ? "warning" : "default";
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0, borderColor: failed.length > unjudged.length ? "error.main" : failed.length ? "warning.main" : undefined }}
      component="section" aria-label={group.persona ? `Persona ${group.name}` : "The whole run"}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="subtitle2" sx={{ ...HELD, ...(p ? MONO : {}) }}>{group.name}</Typography>
          {group.status ? <Mark tone={tone} strong={group.status === "failed"} label={personaWord(group.status)} /> : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {group.counts.total} {group.counts.total === 1 ? "check" : "checks"}: {group.counts.fail} failed{unjudged.length ? ` (${unjudged.length} inconclusive, not a finding about the app)` : ""},{" "}
          {group.counts.skipped} skipped, {group.counts.pass} passed
          {p?.routes?.length ? ` · ${p.routes.length} ${p.routes.length === 1 ? "screen" : "screens"} reached by a bounded crawl` : ""}.
        </Typography>
        {p && accessLine(p) ? (
          <Typography variant="body2" sx={HELD}>{accessLine(p)}</Typography>
        ) : null}
        {failed.length || skipped.length ? (
          <Box component="ul" sx={{ m: 0, p: 0 }}>
            {[...failed, ...skipped].map((c) => <CheckRow key={`${c.id}:${c.route || ""}`} check={c} get={get} requestId={requestId} runId={runId} />)}
          </Box>
        ) : null}
        {passed.length ? (
          <Disclose label="Passed checks" count={passed.length}
            summary={`${passed.length} ${passed.length === 1 ? "check" : "checks"} found nothing, on synthetic data.`}>
            <Box component="ul" sx={{ m: 0, p: 0 }}>
              {passed.map((c) => <CheckRow key={c.id} check={c} get={get} requestId={requestId} runId={runId} />)}
            </Box>
          </Disclose>
        ) : null}
        {p && (p.routes?.length || shots.length || p.notes?.length || p.truncated) ? (
          <Disclose label="What it reached" summary="What the run reached as this persona, and where it stopped."
            align="row">
            <Stack spacing={1.5}>
              {p.truncated ? <Typography variant="body2" sx={HELD}>The crawl stopped early: {String(p.truncated)}. A screen it never reached is not checked.</Typography> : null}
              <Routes persona={p} />
              {(p.notes || []).map((n, i) => <Typography key={i} variant="body2" color="text.secondary" sx={HELD}>{n}</Typography>)}
              {shots.map((s) => (
                <Screenshot key={s.path} get={get} requestId={requestId} runId={runId} path={s.path} label={s.label || s.path} />
              ))}
            </Stack>
          </Disclose>
        ) : null}
      </Stack>
    </Paper>
  );
}

// -- the logs -------------------------------------------------------------------------

function LogShell({ title, line, lineTone, log, enabledText, children }) {
  return (
    <Stack spacing={1} component="section" aria-label={title}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="subtitle2">{title}</Typography>
        {log.phase === "ready" ? (
          <Button size="small" onClick={log.load}>Read again</Button>
        ) : null}
      </Stack>
      {log.phase === "idle" ? (
        <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
          <Typography variant="body2" color="text.secondary">{enabledText}</Typography>
          <Button size="small" variant="outlined" onClick={log.load}>Read it now</Button>
        </Stack>
      ) : null}
      {log.phase === "loading" ? <Loading label={`Reading the ${title.toLowerCase()}`} minHeight={48} size={20} /> : null}
      {log.phase === "failed" ? (
        <Stack spacing={1}>
          <Said failure={log.failure} title={`The ${title.toLowerCase()} could not be read`} />
          <Box><Button size="small" variant="outlined" onClick={log.load}>Try again</Button></Box>
        </Stack>
      ) : null}
      {log.phase === "ready" ? (
        <>
          {lineTone ? (
            <Alert severity={lineTone} variant="outlined" role="status">{line}</Alert>
          ) : (
            <Typography variant="body2" sx={HELD}>{line}</Typography>
          )}
          {children}
        </>
      ) : null}
    </Stack>
  );
}

export function CallLog({ log }) {
  const calls = log.body;
  const unused = calls?.unused_grants || [];
  const personas = Object.entries(calls?.summary?.by_persona || {}).map(([persona, v]) => ({
    persona, total: v.total,
    outcomes: Object.entries(v.by_outcome || {}).map(([k, n]) => `${k} ${n}`).join(" · "),
  }));
  return (
    <LogShell title="Kernel calls" log={log} line={callsLine(calls)}
      enabledText="Read once a QA run has finished. Calls you make by hand in the sandbox are logged too.">
      {calls ? (
        <>
          <Typography variant="body2" sx={HELD}>
            {unused.length
              ? `${unused.length} requested ${unused.length === 1 ? "grant was" : "grants were"} never used in this sandbox. That is a reason to ask whether the app needs ${unused.length === 1 ? "it" : "them"}, not a finding.`
              : "Every requested grant was used by at least one recorded call."}
          </Typography>
          <Disclose label="Calls and grants" align="row"
            summary="Calls by persona, the grants nothing used, and each call.">
            <Stack spacing={1.5}>
              {unused.length ? (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {unused.map((g, i) => (
                    <Typography component="li" variant="body2" key={i} sx={HELD}>
                      <Chip size="small" variant="outlined" label={`unused ${GRANT_KIND[g.kind] || g.kind}`} sx={{ mr: 0.75 }} />
                      {g.detail}
                    </Typography>
                  ))}
                </Box>
              ) : null}
              <DataTable rows={personas} getRowKey={(r) => r.persona} emptyTitle="No persona has made a call"
                columns={[
                  { key: "persona", label: "Persona", mono: true },
                  { key: "total", label: "Calls", align: "right" },
                  { key: "outcomes", label: "How they were answered" },
                ]} />
              <DataTable rows={(calls.calls || []).slice(0, 50)} getRowKey={(r) => r.call_id} emptyTitle="No call logged"
                columns={[
                  { key: "at", label: "When", render: (r) => when(r.at) },
                  { key: "persona", label: "Persona", mono: true },
                  { key: "route", label: "Call", render: (r) => `${r.method} ${r.route}${r.verb ? ` · ${r.verb}` : ""}` },
                  { key: "outcome", label: "Answer", render: (r) => `${r.outcome} (${r.status})` },
                ]}
                caption={calls.has_more || (calls.calls || []).length > 50
                  ? "The first 50 calls. The counts above are over every recorded call." : undefined} />
              {(calls.does_not_prove || []).map((t, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: "block", ...HELD }}>{t}</Typography>
              ))}
            </Stack>
          </Disclose>
        </>
      ) : null}
    </LogShell>
  );
}

/**
 * The egress log, and whether the gateway that fills it was working (LO-193).
 *
 * An empty list is two different facts and is worded as two: **nothing was
 * contacted** (the gateway reported and recorded no attempt) and **not recorded**
 * (it was not working, so the list says nothing about the function). The second is
 * a warning and not a plain sentence, the gateway is a word beside the list and not
 * a colour, and what a working gateway does not prove sits on that word, where it
 * is read, rather than at the foot of the panel.
 */
export function EgressLog({ log }) {
  const egress = log.body;
  const s = egress?.summary || {};
  const hosts = egress?.hosts || [];
  const gateway = gatewayView(egress);
  const reading = egressReading(egress);
  // A gateway the kernel said was not working: what was not tried is not known.
  const notWorking = gateway && gateway.state !== "unknown" && !gateway.healthy;
  return (
    <LogShell title="Egress log" log={log} line={egressLine(egress)}
      lineTone={reading === "not_recorded" ? "warning" : undefined}
      enabledText="Read once a QA run has finished. It lists the hosts the app's server function tried to reach.">
      {egress ? (
        <>
          {gateway ? (
            <Stack spacing={0.5} component="section" aria-label="Egress gateway">
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="caption" color="text.secondary">Gateway</Typography>
                <Mark tone={gateway.tone} strong={!gateway.healthy} label={gateway.word} />
              </Stack>
              {gateway.detail ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ...HELD }}>{gateway.detail}</Typography>
              ) : null}
              {gateway.lastError ? (
                <Typography variant="caption" sx={{ display: "block", ...HELD }}>
                  Its last failed write{gateway.lastErrorAt ? `, ${when(gateway.lastErrorAt)}` : ""}: <span style={MONO}>{gateway.lastError}</span>
                </Typography>
              ) : null}
              {gateway.healthy ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", ...HELD }}>
                  Working means it reported in the last few minutes and its writes landed. It does not mean every attempt in a run was seen.
                </Typography>
              ) : null}
            </Stack>
          ) : null}
          {(s.allowed_not_declared || []).length ? (
            <Alert severity="warning" variant="outlined">
              Let through but not declared now: {s.allowed_not_declared.join(", ")}. The request changed after these were allowed.
            </Alert>
          ) : null}
          <Disclose label="Hosts" align="row"
            summary="Each host, and what the log cannot show.">
            <Stack spacing={1.5}>
              <DataTable rows={hosts} getRowKey={(r) => `${r.host}:${r.allowed}`}
                emptyTitle={notWorking ? "No host recorded, which is not the same as none tried" : "No host tried"}
                columns={[
                  { key: "host", label: "Host", mono: true },
                  { key: "allowed", label: "Result", render: (r) => (r.allowed ? "allowed" : "blocked") },
                  { key: "count", label: "Attempts", align: "right" },
                  { key: "last_seen", label: "Last", render: (r) => when(r.last_seen) },
                ]} />
              <Typography variant="body2" sx={HELD}>
                Declared by the request: {(egress.declared || []).length ? egress.declared.join(", ") : "no host"}.
                {(s.declared_not_contacted || []).length
                  ? (notWorking
                    ? ` Whether ${s.declared_not_contacted.join(", ")} ${s.declared_not_contacted.length === 1 ? "was" : "were"} tried is not recorded, because the gateway is not confirmed working.`
                    : ` Declared and never tried: ${s.declared_not_contacted.join(", ")}. That is a reason to ask whether the app needs ${s.declared_not_contacted.length === 1 ? "it" : "them"}, not a finding.`)
                  : ""}
              </Typography>
              {(egress.kernel_hosts || []).length ? (
                <Typography variant="body2" color="text.secondary" sx={HELD}>
                  The sandbox's own calls to the kernel are listed apart: {egress.kernel_hosts.map((h) => h.host).join(", ")}.
                </Typography>
              ) : null}
              {(egress.does_not_prove || []).map((t, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: "block", ...HELD }}>{t}</Typography>
              ))}
            </Stack>
          </Disclose>
        </>
      ) : null}
    </LogShell>
  );
}

// -- the panel ------------------------------------------------------------------------

/**
 * `onSubmit(requestId)` sends the draft for approval and throws what the kernel
 * refused with; without it the panel posts to the kernel itself. `onSubmitted`
 * is told once it has gone, so a list that shows the request's state can read
 * it again.
 *
 * Beside a chat (LO-206) it is also given `chat`, the chat's view, and says when
 * the chat has moved past this draft (`revisionOf`): a revision being built holds
 * Submit with the reason beside it and puts this sandbox's results under
 * "Previous build", dated, while the new version's progress is on top. `asked` is a
 * message this tab has just sent, `previous` the draft the one on screen replaced,
 * and `onDraft` is told which draft this is, so the chat page can say the last.
 * On an app's own page there is no chat, so there is nothing to be past.
 */
export default function SandboxPanel({ get, post, requestId, onSubmit, onSubmitted, chat, asked, previous, onDraft }) {
  const data = useSandbox(get, requestId);
  const head = data.head || requestId;
  const { request, box, qa, qaFailure, again } = data;
  const run = runOf(qa);

  // How many saves the chat held when this panel first saw it: a save past that is a
  // newer draft on its way. Taken once, from the first view the panel is given.
  const savesAtMount = useRef(null);
  if (savesAtMount.current === null && chat?.messages) savesAtMount.current = countSaves(chat.messages);
  const revision = useMemo(
    () => revisionOf({ chat, request, asked, savesAtMount: savesAtMount.current }),
    [chat, request, asked],
  );
  const stale = revision?.working ? revision : null;
  const moved = revision && !revision.working ? revision : null;

  // Tells the chat page which draft is on screen, so the panel for the next one can
  // say which it replaced.
  const draftId = request?.request_id || "";
  const draftBuild = request?.proposed?.build_id || "";
  const draftSaved = request?.requested_at || "";
  const draftOpen = request ? OPEN.includes(request.state) : false;
  useEffect(() => {
    if (draftId && draftOpen) onDraft?.({ id: draftId, buildId: draftBuild, savedAt: draftSaved });
  }, [draftId, draftBuild, draftSaved, draftOpen, onDraft]);
  const [gone, setGone] = useState(false);

  const list = useMemo(() => steps(box, qa), [box, qa]);
  const gate = submitGate({ request, qa, qaFailure, box, revision });
  const summary = headline(qa);
  const groups = useMemo(() => groupsOf(run), [run]);

  // The two logs are read when a run has an outcome, and read again for a new one.
  const finished = run && run.status !== "running" ? `${run.run_id}:${run.status}` : "";
  const calls = useLog(get, `/sandboxes/${head}/calls`, finished);
  const egress = useLog(get, `/sandboxes/${head}/egress`, finished);

  const [submitting, setSubmitting] = useState(false);
  const [refused, setRefused] = useState(null);
  const [sent, setSent] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    setRefused(null);
    try {
      if (onSubmit) await onSubmit(head);
      else await post(`/manifest-requests/${head}/submit`, {});
      setSent(true);
      onSubmitted?.();
    } catch (failure) {
      setRefused(failure);
    } finally {
      setSubmitting(false);
      again();
    }
  };

  const [starting, setStarting] = useState(false);
  const [startRefused, setStartRefused] = useState(null);
  const start = async () => {
    setStarting(true);
    setStartRefused(null);
    try {
      await post(`/sandboxes/${head}/qa`, {});
    } catch (failure) {
      setStartRefused(failure);
    } finally {
      setStarting(false);
      again();
    }
  };

  const [opening, setOpening] = useState(false);
  const [openFailed, setOpenFailed] = useState("");
  // The tab is opened on the click, before the awaits, or the browser blocks it
  // as a pop-up. Its opener is cut before it leaves: the page it is about to
  // show is code nobody has approved, and it must not be able to reach back.
  const open = async () => {
    setOpening(true);
    setOpenFailed("");
    const tab = window.open("about:blank", "_blank");
    try {
      if (!tab) throw new Error("Your browser blocked the new tab. Allow pop-ups for Builder and try again.");
      const href = viewerUrl(box.appUrl, await freshToken());
      if (!href) throw new Error("There is no address to open, or no sign-in to open it with.");
      try { tab.opener = null; } catch { /* already cut */ }
      tab.location.href = href;
    } catch (failure) {
      if (tab) tab.close();
      setOpenFailed(failure.message);
    } finally {
      setOpening(false);
    }
  };

  const reasonId = useId();

  if (data.loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
        <Typography variant="overline" color="text.secondary">Sandbox</Typography>
        <Loading label="Reading the sandbox" minHeight={60} size={20} />
      </Paper>
    );
  }

  const state = box?.state || "none";
  const closed = request && !OPEN.includes(request.state);
  // A sandbox that stalled is said by the notice below, which carries the same
  // sentence as the state's detail and each attempt's reason: not twice.
  const stallShown = Boolean(box?.recovery)
    && ["stalled", "replaced", "failed"].includes(box.recovery.state)
    && ["provisioning", "failed"].includes(box.state);

  // Everything about the sandbox this panel is on, apart from Submit: what it is, how it
  // got there, the report and the logs. Said once and placed twice, because while a
  // revision is being built it is the previous build's, and collapses.
  const detail = (
    <>
      {stallShown ? null : (
        <Typography variant="body2" sx={HELD}>
          {box ? box.detail
            : "No sandbox yet. One is made when the builder saves a draft that names a build, and takes ten minutes or so."}
        </Typography>
      )}
      {box?.recovery ? <RecoveryNotice recovery={box.recovery} /> : null}
      <Steps list={list} />
      <SandboxUpkeep upkeep={request?.sandbox_upkeep} get={get} post={post} />
      {data.quiet ? (
        <Typography variant="caption" color="text.secondary">
          Nothing has changed for a while, so this stopped looking. Check again looks now.
        </Typography>
      ) : null}

      <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
        {box?.appUrl ? (
          <>
            <Button size="small" variant="outlined" onClick={open} disabled={opening}>
              {opening ? "Opening…" : "Open the sandbox"}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={HELD}>
              <span style={MONO}>{box.appUrl}</span>. Opens in a new tab with you signed in as
              yourself; the page then asks which persona to be. It shows synthetic data and says so.
            </Typography>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {state === "ready"
              ? "The sandbox has no address to open yet."
              : "The link to open the sandbox appears when it is ready."}
          </Typography>
        )}
        {openFailed ? <Alert severity="warning" variant="outlined" role="alert">{openFailed}</Alert> : null}
      </Stack>
    </>
  );

  const report = (
    <Stack spacing={1} component="section" aria-label="QA report">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Typography variant="subtitle2">QA report</Typography>
        <Mark tone={RUN_TONE[summary.tone]} strong={summary.tone === "failed"} label={summary.word} />
      </Stack>
      {summary.text ? <Typography variant="body2" sx={HELD}>{summary.text}</Typography> : null}
      {run ? (
        <Typography variant="caption" color="text.secondary" sx={HELD}>
          Run <span style={MONO}>{run.run_id}</span>
          {run.started_at ? `, started ${when(run.started_at)}` : ""}
          {run.finished_at ? `, finished ${when(run.finished_at)}` : ""}.
        </Typography>
      ) : null}
      {run && qa?.does_not_prove?.length ? (
        <Disclose label="What this does not show"
          summary="A run covers the checks it lists, for the personas it names, on the screens a bounded crawl reached.">
          <Stack spacing={0.75}>
            {qa.does_not_prove.map((t, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={HELD}>{t}</Typography>
            ))}
          </Stack>
        </Disclose>
      ) : null}
      {run?.live_run && run.live_run.ran === false ? (
        <Alert severity="info" variant="outlined">
          The live run was skipped ({String(run.live_run.skipped_reason || "no reason given").replace(/_/g, " ")})
          {run.live_run.detail ? `: ${run.live_run.detail}` : "."} Nothing was explored, which is not a pass.
        </Alert>
      ) : null}
      {qaFailure && !run ? <Said failure={qaFailure} title="The QA result could not be read" /> : null}
      {state === "ready" ? (
        <Stack spacing={0.75} sx={{ alignItems: "flex-start" }}>
          <Button size="small" variant="outlined" onClick={start}
            disabled={starting || run?.status === "running"}>
            {starting ? "Starting…" : run ? "Run QA again" : "Run QA now"}
          </Button>
          {startRefused ? <Said failure={startRefused} title="The run was not started" /> : null}
        </Stack>
      ) : null}
    </Stack>
  );

  const submitSection = (
    <Stack spacing={1} component="section" aria-label="Submit for approval">
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <GuardedButton can={gate.can && !submitting} reasonId={reasonId} onClick={submit}>
          {submitting ? "Submitting…" : "Submit for approval"}
        </GuardedButton>
      </Stack>
      {gate.can ? (
        <Typography variant="body2" color="text.secondary" sx={HELD}>
          A run passed for this build and these permissions. Submitting sends the draft to the governors;
          it approves nothing, and the kernel decides whether it goes.
        </Typography>
      ) : (
        <Typography id={reasonId} variant="body2" color="text.secondary" sx={HELD}>
          {gate.reason}
        </Typography>
      )}
      {gate.basis ? (
        <Typography variant="caption" color="text.secondary" sx={HELD}>{gate.basis}</Typography>
      ) : null}
      {sent && request?.state !== "pending" ? (
        <Alert severity="success" variant="outlined">Sent for approval.</Alert>
      ) : null}
      {refused ? <Said failure={refused} title="Not submitted: the kernel refused this" /> : null}
    </Stack>
  );

  const rest = (
    <>
      {groups.length ? (
        <Stack spacing={1.5}>
          {groups.map((g) => <Group key={g.key} group={g} get={get} requestId={head} runId={run?.run_id} />)}
        </Stack>
      ) : null}

      <Divider />
      <CallLog log={calls} />
      <Divider />
      <EgressLog log={egress} />
    </>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, ...FOCUS_RING }} component="section" aria-label="Sandbox">
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="overline" color="text.secondary">Sandbox</Typography>
          {stale ? (
            <Mark tone="warning" label={stale.chip} />
          ) : (
            <Mark tone={STATE_TONE[state] || "default"} strong={state === "failed"} label={STATE_WORD[state] || state} />
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={again}>Check again</Button>
        </Stack>

        {previous && !gone && !stale ? (
          <Alert severity="info" variant="outlined" role="status" onClose={() => setGone(true)}>
            <AlertTitle>A new draft, and a new sandbox</AlertTitle>
            <Typography variant="body2" sx={HELD}>{replacedLine(previous)}</Typography>
          </Alert>
        ) : null}

        {data.requestFailure ? <Said failure={data.requestFailure} title="The request could not be read" /> : null}
        {closed ? (
          <Typography variant="body2" sx={HELD}>
            This request is {request.state === "superseded" ? "replaced" : request.state}. Its sandbox goes with it.
          </Typography>
        ) : null}
        {data.controlFailure ? (
          <Alert severity="warning" variant="outlined">
            The sandbox's build state could not be read just now: {refusalWords(data.controlFailure).text || "no answer"}.
            What the kernel knows of it is shown without it.
          </Alert>
        ) : null}

        {!closed && !data.requestFailure ? (
          stale ? (
            <>
              <RevisionNotice revision={stale} />
              <RevisionSteps list={stale.steps} />
              {submitSection}
              <Disclose label="Previous build"
                summary={previousBuildLine({ draft: stale.draft, box, qa })}>
                <Stack spacing={1.5} component="section" aria-label="Previous build">
                  {detail}
                  <Divider />
                  {report}
                  {rest}
                </Stack>
              </Disclose>
            </>
          ) : (
            <>
              {moved ? <RevisionNotice revision={moved} /> : null}
              {detail}
              <Divider />
              {report}
              {submitSection}
              {rest}
            </>
          )
        ) : null}
      </Stack>
    </Paper>
  );
}
